import { z } from 'zod';
import { idSchema } from './common.js';
/**
 * Website Studio (0067) — the validation contract. Everything a fleet
 * can store passes through here; the DB holds only what these schemas
 * admit. North star: a non-technical owner CANNOT break their site —
 * so every plain-text field is capped and refuses markup outright, the
 * rich-text-LITE type is a strict ProseMirror-JSON whitelist (unknown
 * node or mark types are REJECTED AT WRITE TIME — binding condition,
 * approved 2026-08-07), and the section union rejects unknown types.
 */
// ---- plain text: capped, markup refused loudly ---------------------------
const NO_MARKUP = /^[^<>]*$/;
function plain(max) {
    return z
        .string()
        .trim()
        .max(max)
        .regex(NO_MARKUP, 'Text cannot contain < or >');
}
/** Uploaded-image URL — our public media host only, set via image-init. */
const imageUrlSchema = z.string().trim().url().max(500);
// ---- rich-text-LITE: ProseMirror JSON whitelist --------------------------
// Allowed nodes: doc, paragraph, bulletList, orderedList, listItem, text.
// Allowed marks: bold, italic. Everything else fails the save.
export const richTextMarkSchema = z
    .object({
    type: z.enum(['bold', 'italic']),
})
    .strict();
export const richTextTextNodeSchema = z
    .object({
    type: z.literal('text'),
    text: z.string().max(2000),
    marks: z.array(richTextMarkSchema).max(2).optional(),
})
    .strict();
export const richTextParagraphSchema = z
    .object({
    type: z.literal('paragraph'),
    content: z.array(richTextTextNodeSchema).max(100).optional(),
})
    .strict();
export const richTextListItemSchema = z
    .object({
    type: z.literal('listItem'),
    content: z.array(richTextParagraphSchema).min(1).max(10),
})
    .strict();
export const richTextListSchema = z
    .object({
    type: z.enum(['bulletList', 'orderedList']),
    content: z.array(richTextListItemSchema).min(1).max(20),
})
    .strict();
export const richTextDocSchema = z
    .object({
    type: z.literal('doc'),
    content: z
        .array(z.union([richTextParagraphSchema, richTextListSchema]))
        .min(1)
        .max(40),
})
    .strict();
// ---- highlight presets (shared with the old highlights slot idea) --------
export const HIGHLIGHT_PRESETS = [
    'same_day',
    'multi_drop',
    'fragile',
    'out_of_hours',
    'nationwide',
    'refrigerated',
    'pallets',
    'two_man',
];
export const highlightItemSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('preset'), value: z.enum(HIGHLIGHT_PRESETS) }).strict(),
    z.object({ kind: z.literal('custom'), value: plain(40).pipe(z.string().min(1)) }).strict(),
]);
// ---- section content, one schema per type --------------------------------
export const heroContentSchema = z
    .object({
    headline: plain(60).optional(),
    subline: plain(120).optional(),
    imageUrl: imageUrlSchema.optional(),
})
    .strict();
export const aboutContentSchema = z
    .object({
    heading: plain(60).optional(),
    body: plain(600).optional(),
    photoUrl: imageUrlSchema.optional(),
})
    .strict();
export const highlightsContentSchema = z
    .object({
    items: z.array(highlightItemSchema).min(3).max(6),
})
    .strict();
export const galleryContentSchema = z
    .object({
    images: z
        .array(z.object({ url: imageUrlSchema, caption: plain(80).optional() }).strict())
        .max(8),
})
    .strict();
export const faqContentSchema = z
    .object({
    items: z
        .array(z.object({ q: plain(80).pipe(z.string().min(1)), a: plain(400).pipe(z.string().min(1)) }).strict())
        .max(8),
})
    .strict();
/** Contact renders from Fleet Settings; per-page overrides + toggles only. */
export const contactContentSchema = z
    .object({
    overrides: z
        .object({
        phone: plain(20).optional(),
        email: z.string().trim().email().max(120).optional(),
        address: plain(160).optional(),
        openingHours: plain(160).optional(),
    })
        .strict()
        .optional(),
    show: z
        .object({
        phone: z.boolean().optional(),
        email: z.boolean().optional(),
        address: z.boolean().optional(),
        openingHours: z.boolean().optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const coverageContentSchema = z
    .object({
    areas: z.array(plain(40).pipe(z.string().min(1))).max(12),
})
    .strict();
export const textContentSchema = z
    .object({
    doc: richTextDocSchema,
})
    .strict();
export const ctaContentSchema = z
    .object({
    /** Button label is fixed by the design system — the line is the slot. */
    line: plain(80).optional(),
})
    .strict();
export const sectionContentSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('hero'), content: heroContentSchema }).strict(),
    z.object({ type: z.literal('about'), content: aboutContentSchema }).strict(),
    z.object({ type: z.literal('highlights'), content: highlightsContentSchema }).strict(),
    z.object({ type: z.literal('gallery'), content: galleryContentSchema }).strict(),
    z.object({ type: z.literal('faq'), content: faqContentSchema }).strict(),
    z.object({ type: z.literal('contact'), content: contactContentSchema }).strict(),
    z.object({ type: z.literal('coverage'), content: coverageContentSchema }).strict(),
    z.object({ type: z.literal('text'), content: textContentSchema }).strict(),
    z.object({ type: z.literal('cta'), content: ctaContentSchema }).strict(),
]);
// ---- pages ---------------------------------------------------------------
/** Slugs the site can never claim — system surfaces + infrastructure. */
export const RESERVED_PAGE_SLUGS = [
    'book',
    'track',
    't',
    'login',
    'loads',
    'invoices',
    'account',
    'invite',
    'api',
    'assets',
    'home',
];
export const pageSlugSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'Lowercase letters, digits, hyphens')
    .refine((s) => !RESERVED_PAGE_SLUGS.includes(s), 'That address is reserved');
export const createPageBodySchema = z
    .object({
    title: plain(60).pipe(z.string().min(1)),
})
    .strict();
export const updatePageBodySchema = z
    .object({
    title: plain(60).pipe(z.string().min(1)).optional(),
    navVisible: z.boolean().optional(),
    seoTitle: plain(70).nullable().optional(),
    seoDescription: plain(160).nullable().optional(),
})
    .strict();
export const reorderBodySchema = z
    .object({
    orderedIds: z.array(idSchema).min(1).max(50),
})
    .strict();
// ---- sections API bodies -------------------------------------------------
export const createSectionBodySchema = sectionContentSchema;
export const updateSectionBodySchema = z
    .object({
    enabled: z.boolean().optional(),
    envelope: sectionContentSchema.optional(),
})
    .strict();
// ---- brand draft ---------------------------------------------------------
const hexSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const siteBrandBodySchema = z
    .object({
    brandPrimaryHex: hexSchema.nullable().optional(),
    brandAccentHex: hexSchema.nullable().optional(),
    logoUrl: imageUrlSchema.nullable().optional(),
})
    .strict();
// ---- params --------------------------------------------------------------
export const sitePageParamSchema = z.object({
    fleetId: idSchema,
    pageId: idSchema,
});
export const siteSectionParamSchema = z.object({
    fleetId: idSchema,
    sectionId: idSchema,
});
export const THEME_PRESETS = [
    { key: 'harbour', name: 'Harbour', primaryHex: '#13293d', accentHex: '#f2a03d' },
    { key: 'signal', name: 'Signal', primaryHex: '#1a1a1a', accentHex: '#c72027' },
    { key: 'forest', name: 'Forest', primaryHex: '#1f3b2c', accentHex: '#8fce5e' },
    { key: 'slate', name: 'Slate', primaryHex: '#243b4a', accentHex: '#6fb7e0' },
    { key: 'plum', name: 'Plum', primaryHex: '#3b2340', accentHex: '#e0a83c' },
    { key: 'charcoal', name: 'Charcoal', primaryHex: '#2b2b2b', accentHex: '#f28c38' },
    { key: 'deep-teal', name: 'Deep teal', primaryHex: '#123c3c', accentHex: '#ff8b70' },
    { key: 'midnight', name: 'Midnight', primaryHex: '#101d33', accentHex: '#7fe0c0' },
];
// ---- site image uploads (logo pipeline, public media bucket) -----------
export const SITE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const siteImageInitBodySchema = z
    .object({
    kind: z.enum(['hero', 'about', 'gallery']),
    mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
    sizeBytes: z.number().int().positive().max(SITE_IMAGE_MAX_BYTES),
})
    .strict();
//# sourceMappingURL=site.js.map